import { Phone, PhoneMissed, PhoneIncoming, PhoneOutgoing, Sparkles, Mic, MicOff } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCallDate, formatCallDuration } from '@/lib/dateUtils';
import { useProcessCallInsights } from '@/hooks/useProcessCallInsights';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface CallHistoryItemProps {
  call: {
    id: string;
    callType: 'missed' | 'incoming' | 'outgoing';
    displayName: string;
    displayNumber: string;
    created_at: string;
    duration_seconds: number | null;
    status: string | null;
    hasRecording?: boolean;
  };
  onCallBack: (phoneNumber: string) => void;
}

export function CallHistoryItem({ call, onCallBack }: CallHistoryItemProps) {
  const { processCallInsights, isProcessing } = useProcessCallInsights();
  
  const canGenerateInsights = 
    call.duration_seconds && 
    call.duration_seconds >= 60 && 
    call.hasRecording &&
    call.status === 'completed';
  
  const getCallIcon = () => {
    switch (call.callType) {
      case 'missed':
        return <PhoneMissed className="h-4 w-4 text-destructive" />;
      case 'incoming':
        return <PhoneIncoming className="h-4 w-4 text-green-600 dark:text-green-400" />;
      case 'outgoing':
        return <PhoneOutgoing className="h-4 w-4 text-primary" />;
    }
  };

  const getCallLabel = () => {
    switch (call.callType) {
      case 'missed':
        return 'Perdida';
      case 'incoming':
        return 'Recebida';
      case 'outgoing':
        return 'Realizada';
    }
  };

  const initials = call.displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const duration = formatCallDuration(call.duration_seconds);

  return (
    <div 
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors cursor-pointer group"
      onClick={() => onCallBack(call.displayNumber)}
    >
      <div className="relative">
        <Avatar className="h-10 w-10">
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-0.5">
          {getCallIcon()}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm truncate">{call.displayName}</p>
          {call.callType === 'missed' && (
            <Badge variant="destructive" className="text-xs">Perdida</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{getCallLabel()}</span>
          <span>•</span>
          <span>{formatCallDate(call.created_at)}</span>
          {duration && (
            <>
              <span>•</span>
              <span>{duration}</span>
            </>
          )}
          <span>•</span>
          <span className="flex items-center gap-1">
            {call.hasRecording ? (
              <>
                <Mic className="h-3 w-3 text-primary" />
                Gravada
              </>
            ) : (
              <>
                <MicOff className="h-3 w-3" />
                Sem gravação
              </>
            )}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1">
        {canGenerateInsights && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  disabled={isProcessing}
                  onClick={(e) => {
                    e.stopPropagation();
                    processCallInsights(call.id);
                  }}
                >
                  <Sparkles className="h-4 w-4 text-primary" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Gerar Insights com IA</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        <Button
          variant="ghost"
          size="icon"
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            onCallBack(call.displayNumber);
          }}
        >
          <Phone className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
